/**
 * UK local-time helpers. All storage is UTC epoch ms; these convert to and from
 * Europe/London wall-clock time, handling GMT/BST changes.
 */
export const UK_TZ = "Europe/London";

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday
}

const partsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: UK_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  weekday: "short",
  hourCycle: "h23",
});

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function localParts(ms: number, timeZone: string = UK_TZ): LocalParts {
  const fmt =
    timeZone === UK_TZ
      ? partsFormatter
      : new Intl.DateTimeFormat("en-GB", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          weekday: "short",
          hourCycle: "h23",
        });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(new Date(ms))) p[part.type] = part.value;
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour: Number(p.hour) % 24,
    minute: Number(p.minute),
    second: Number(p.second),
    weekday: WEEKDAYS[p.weekday ?? "Sun"] ?? 0,
  };
}

const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/** YYYY-MM-DD in UK local time. */
export function localDate(ms: number, timeZone: string = UK_TZ): string {
  const p = localParts(ms, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** HH:MM (24h) in UK local time. */
export function localTime(ms: number, timeZone: string = UK_TZ): string {
  const p = localParts(ms, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** DD/MM/YYYY in UK local time (QuickBooks UK date format). */
export function ukDate(ms: number, timeZone: string = UK_TZ): string {
  const p = localParts(ms, timeZone);
  return `${pad(p.day)}/${pad(p.month)}/${p.year}`;
}

function offsetMs(ms: number, timeZone: string): number {
  const p = localParts(ms, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * Converts a UK wall-clock date/time to UTC epoch ms. For times that occur twice
 * (clocks going back) the first occurrence (BST) is returned; for times that do
 * not exist (clocks going forward) the result is shifted forward by the gap.
 */
export function zonedToUtc(date: string, time = "00:00", timeZone: string = UK_TZ): number {
  const [y, mo, d] = date.split("-").map(Number) as [number, number, number];
  const [h, mi] = time.split(":").map(Number) as [number, number];
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const t1 = guess - offsetMs(guess, timeZone);
  const t2 = guess - offsetMs(t1, timeZone);
  const wanted = `${date} ${pad(h)}:${pad(mi)}`;
  const matches = (t: number) => `${localDate(t, timeZone)} ${localTime(t, timeZone)}` === wanted;
  const valid = [t1 - 3_600_000, t1, t2].filter(matches);
  if (valid.length > 0) return Math.min(...valid);
  // Non-existent wall time (spring forward): move forward by the gap.
  return Math.max(t1, t2);
}

/** Start (inclusive) of a UK local date, as UTC ms. */
export function startOfLocalDay(date: string, timeZone: string = UK_TZ): number {
  return zonedToUtc(date, "00:00", timeZone);
}

/** Adds whole days to a YYYY-MM-DD string. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** UTC ms range [start, end) covering UK local dates from..to inclusive. */
export function localDateRange(from: string, to: string, timeZone: string = UK_TZ): [number, number] {
  return [startOfLocalDay(from, timeZone), startOfLocalDay(addDays(to, 1), timeZone)];
}

export function isIsoDate(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

export function isHhMm(s: unknown): s is string {
  return typeof s === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

/** Weekday (0 = Sunday) of a YYYY-MM-DD date. */
export function weekdayOf(date: string): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Minutes rounded to the nearest multiple of `step`. */
export function roundMinutes(minutes: number, step: number): number {
  if (step <= 1) return Math.round(minutes);
  return Math.round(minutes / step) * step;
}

/** Pay period containing `date`, given the period type and an anchor Monday. */
export function payPeriodFor(
  date: string,
  period: "weekly" | "fortnightly" | "monthly",
  anchor: string,
): { start: string; end: string } {
  if (period === "monthly") {
    const [y, m] = date.split("-").map(Number) as [number, number];
    const start = `${y}-${pad(m)}-01`;
    const nextMonth = m === 12 ? `${y + 1}-01-01` : `${y}-${pad(m + 1)}-01`;
    return { start, end: addDays(nextMonth, -1) };
  }
  const len = period === "weekly" ? 7 : 14;
  const dayMs = 86_400_000;
  const [ay, am, ad] = anchor.split("-").map(Number) as [number, number, number];
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const diff = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ay, am - 1, ad)) / dayMs);
  const offset = ((diff % len) + len) % len;
  const start = addDays(date, -offset);
  return { start, end: addDays(start, len - 1) };
}
